import {
  DeleteOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Space } from 'antd';
import * as Component from 'components';
import { CONTENT_TYPE } from 'constants/constant';
import { contentService } from 'content/service';
import { ContentInterface } from 'content/type';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contentApiService } from 'service/api/contentApiService';
import { accountStore } from 'store/accountStore';
import { TableColumnInterface } from 'type/tableType';
import { slugify } from 'utils/slugify';
import ContentBulkDistributionButton from '../components/main/ContentBulkDistributionButton';
import ContentBulkUpdateButton from '../components/main/ContentBulkUpdateButton';
import Preview from '../components/main/Preview';
import { libraryService } from '../service/libraryService';
import './Library.css';

const Library: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '100',
    status: 'draft,published',
  });
  const [selectedRecord, setSelectedRecord] = React.useState<ContentInterface | null>(null);
  const user = accountStore.getAccount();
  const isPlatformOne = user?.platform.id === 1;
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: 'THUMBNAIL',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      width: '15%',
      render: (_text: string, record: ContentInterface): JSX.Element => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            style={{ marginLeft: '10px', width: '100%', height: 'auto' }}
            src={record.thumbnail?.file ?? ''}
            alt=""
          />
        </div>
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '20%',
      sorter: (a: ContentInterface, b: ContentInterface): number => a.title.localeCompare(b.title),
      render: (text: string, record: ContentInterface): JSX.Element => (
        <div
          className="content-title"
          onClick={() =>
            window.open(
              `/${
                record.content_type === 'clip' || record.content_type === 'trailer'
                  ? 'clip-trailer'
                  : 'video'
              }/detail/${record.id || 0}?content_type=${record.content_type}`,
              '_blank'
            )
          }
        >
          <div
            className="title-content"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </div>
      ),
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '20%',
      sorter: (a: ContentInterface, b: ContentInterface): number => a.slug.localeCompare(b.slug),
      render: (text: string, record: ContentInterface): JSX.Element => (
        <div
          className="content-title"
          onClick={() =>
            window.open(
              `/${
                record.content_type === 'clip' || record.content_type === 'trailer'
                  ? 'clip-trailer'
                  : 'video'
              }/detail/${record.id || 0}?content_type=${record.content_type}`,
              '_blank'
            )
          }
        >
          <div
            className="content-title"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </div>
      ),
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      key: 'duration',
      render: (_text: string, record: ContentInterface): string =>
        record.duration ? `${record.duration}` : '',
    },
    {
      title: 'Episode Order',
      dataIndex: 'episode_order',
      key: 'episode_order',
      sorter: (a: ContentInterface, b: ContentInterface): number =>
        (a.episode_order || 0) - (b.episode_order || 0),
      render: (_text: string, record: ContentInterface): string =>
        record.episode_order?.toString() || '',
    },
    {
      title: 'Content Type',
      dataIndex: 'content_type',
      key: 'content_type',
      filters: CONTENT_TYPE.map(type => ({
        text: type,
        value: type,
      })),
      filteredValue: searchParams.get('content_type')
        ? searchParams.get('content_type')?.split(',')
        : null,
      render: (_text: string, record: ContentInterface): string => record.content_type,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      filters: [
        {
          text: 'DRAFT',
          value: 'draft',
        },
        {
          text: 'PUBLISHED',
          value: 'published',
        },
        {
          text: 'UNPUBLISHED',
          value: 'unpublished',
        },
      ],
      filteredValue: searchParams.get('status') ? searchParams.get('status')?.split(',') : null,
      key: 'status',
      render: (_text: string, record: ContentInterface): string => record.status || '',
    },
    {
      title: 'DUBBING MODE',
      dataIndex: 'dubbing_mode',
      key: 'dubbing_mode',
      filters: [
        {
          text: 'Inherit',
          value: 'inherit',
        },
        {
          text: 'Enabled',
          value: 'enabled',
        },
        {
          text: 'Disabled',
          value: 'disabled',
        },
      ],
      filteredValue: searchParams.get('dubbing_modes')
        ? searchParams.get('dubbing_modes')?.split(',')
        : null,
      render: (_text: string, record: ContentInterface): string => record.dubbing_mode,
    },
    isPlatformOne && {
      title: 'DISTRIBUTIONS',
      dataIndex: 'distributions',
      key: 'distributions',
      filters: [
        {
          text: 'Amasian',
          value: 'amasian',
        },
        {
          text: 'Amasian-Staging',
          value: 'amasian-staging',
        },
        {
          text: 'ODK',
          value: 'odk',
        },
        {
          text: 'ODK-Staging',
          value: 'odk-staging',
        },
        {
          text: 'ODC',
          value: 'odc',
        },
        {
          text: 'ODC-Staging',
          value: 'odc-staging',
        },
      ],
      filteredValue: searchParams.get('distributions')
        ? [searchParams.get('distributions') as string]
        : null,
      render: (_text: string, record: ContentInterface): JSX.Element => (
        <span>{record.distributions?.map(d => d.content_consumer_name).join(', ')}</span>
      ),
    },
    {
      title: 'VIDEO',
      dataIndex: 'video',
      key: 'video',
      width: '20%',
      render: (_text: string, record: ContentInterface): JSX.Element => (
        <div style={{ display: 'flex', gap: 8 }}>
          {record.content_stream_manifests && record.content_stream_manifests.length > 0 && (
            <span>hls</span>
          )}
          {record.contents_file && <span>mp4</span>}
        </div>
      ),
    },
    {
      title: 'IMPORTED (UTC)',
      dataIndex: 'created',
      key: 'created',
      sorter: (a: ContentInterface, b: ContentInterface): number =>
        a.created.localeCompare(b.created),
      render: (text: string, _: ContentInterface): string =>
        moment(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      dataIndex: 'actions',
      render: (_text: string, record: ContentInterface): JSX.Element => (
        <Space>
          <Button
            icon={<PlayCircleOutlined />}
            className="action-btn play-btn"
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={() => {
              try {
                const link = document.createElement('a');
                const fileUrl =
                  record.contents_file.file || record.contents_file.external_url || '';
                link.href = fileUrl;
                const fileName = (fileUrl as string).split('/').pop();
                link.setAttribute('download', fileName || 'downloaded-video');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } catch (error) {
                console.error('Error downloading file: ', error);
              }
            }}
            className="action-btn download-btn"
          />
          <Button
            icon={<SettingOutlined />}
            onClick={() => window.open(`/video/detail/${record.id || 0}`, '_blank')}
            className="action-btn setting-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={async () => {
              if (!accountStore.hasPermission('content', 'delete')) {
                message.info('You do not have permission');
                return;
              }

              await contentService.deleteContent(record.id);
              message.success('Deleted successfully');
              return;
            }}
          />
          <Modal
            title="Preview"
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
          >
            {selectedRecord && (
              <Preview
                videoUrl={
                  selectedRecord.contents_file.file ||
                  selectedRecord.content_stream_manifests[0]?.stream_url ||
                  ''
                }
              />
            )}
          </Modal>
        </Space>
      ),
    },
  ].filter(Boolean) as TableColumnInterface<ContentInterface>[];

  const fetchContent = useCallback(
    async (params: URLSearchParams): Promise<{ data: ContentInterface[]; count: number }> => {
      const contentList = await libraryService.fetch(params);
      return { data: contentList.results, count: contentList.count };
    },
    []
  );

  const showCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values: { title: string }) => {
    setLoading(true);
    try {
      const contentData = {
        title: values.title,
        slug: slugify(values.title),
        status: 'draft',
      };

      const newContent = await contentApiService.create(contentData as any);
      message.success('Content created successfully');
      setIsModalVisible(false);
      form.resetFields();

      navigate(`/video/detail/${newContent.id}`);
    } catch (error) {
      console.error('Error creating content:', error);
      message.error('Failed to create content');
    } finally {
      setLoading(false);
    }
  };

  const AddContentButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`+ Add Content`}
        size="small"
        onClick={showCreateModal}
      />
    );
  };

  return (
    <Component.BasePageComponent>
      <div className="video-content-container">
        <Component.TableComponent<ContentInterface>
          name="Content"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          dataFetch={fetchContent}
          columns={columns}
          actions={{ onSelectionChange: setSelectedList }}
          additionalButton={
            [
              <AddContentButton key="add-content" />,
              isPlatformOne ? <ContentBulkDistributionButton selectedList={selectedList} /> : null,
              <ContentBulkUpdateButton key="bulk-update" selectedContentIds={selectedList} />,
            ].filter(Boolean) as JSX.Element[]
          }
        />
      </div>

      <Modal
        title="Create New Content"
        open={isCreateModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '20px' }}>
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: 'Please enter a title!' },
              { min: 2, message: 'Title must be at least 2 characters!' },
            ]}
          >
            <Input placeholder="Enter content title" size="large" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: '30px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Component.BasePageComponent>
  );
};

export default observer(Library);
